/**
 * El aviso de privacidad, en una dirección pública.
 *
 * ## Por qué es una ruta y no un PDF
 *
 * Google Play y App Store piden una **URL** que cualquiera pueda abrir sin
 * instalar nada ni iniciar sesión, y que siga viva mientras la app exista. Un
 * archivo en una carpeta compartida no cumple ninguna de las dos.
 *
 * Vive con la web para que se despliegue con ella: un aviso que describe lo que
 * el sistema hace tiene que cambiar cuando el sistema cambia, y separarlos
 * garantiza que un día digan cosas distintas.
 *
 * ## Lo que hay que revisar antes de publicarlo
 *
 * Esto lo escribió quien conoce el sistema, no un abogado. Los datos técnicos
 * —qué se recoge, dónde vive, cuánto dura— salen del código y son exactos. Lo
 * que hace falta antes de subirlo a una tienda es que alguien con título revise
 * la parte legal, sobre todo **el consentimiento de los datos biométricos**: la
 * LFPDPPP los considera sensibles y exige consentimiento expreso y por escrito,
 * que no se recoge desde una pantalla como esta.
 *
 * Los datos del responsable están marcados con «PENDIENTE» a propósito. Un
 * aviso con el domicilio equivocado es peor que no tenerlo.
 */

const ACTUALIZADO = '2 de septiembre de 2026';

function Titulo({ children }: { children: React.ReactNode }) {
  return <h2 className="text-title-md mt-8 mb-3 text-on-surface">{children}</h2>;
}

function Parrafo({ children }: { children: React.ReactNode }) {
  return <p className="text-body-md mb-3 text-on-surface-variant">{children}</p>;
}

function Lista({ children }: { children: React.ReactNode }) {
  return (
    <ul className="text-body-md mb-3 list-disc space-y-1 pl-6 text-on-surface-variant">
      {children}
    </ul>
  );
}

/** Lo que falta por saber. Se ve, para que nadie lo publique sin llenarlo. */
/**
 * El correo de contacto, pulsable.
 *
 * Quien lee esto lo hace casi siempre desde el teléfono, y lo que quiere es
 * escribir, no copiar una cadena a mano.
 *
 * Donde estaba este componente hubo uno llamado `Pendiente`, que pintaba los
 * huecos del aviso resaltados en ámbar. Era honesto mientras el documento se
 * escribía —se veía a simple vista lo que faltaba— pero las dos tiendas enlazan
 * esta página, así que un aviso con huecos a la vista no se podía publicar.
 */
function Correo({ children }: { children: string }) {
  return (
    <a className="text-primary underline underline-offset-4" href={`mailto:${children}`}>
      {children}
    </a>
  );
}

export function PrivacidadPagina() {
  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      <h1 className="text-headline-md text-on-surface">Aviso de privacidad</h1>
      <p className="text-label-sm mt-2 mb-8 text-outline">Última actualización: {ACTUALIZADO}</p>

      <Titulo>Quién es responsable de tus datos</Titulo>
      <Parrafo>
        Ajolote Technologies, S. de R.L. de C.V., con domicilio en Av. Rafael Buelna 11, 2do piso,
        colonia Sánchez Celis, Mazatlán, Sinaloa, México, C.P. 82120, es responsable del tratamiento
        de los datos personales que se recogen a través de Kodica, el sistema de control de acceso
        instalado en tu fraccionamiento.
      </Parrafo>
      <Parrafo>
        Este aviso se emite conforme a la Ley Federal de Protección de Datos Personales en Posesión
        de los Particulares.
      </Parrafo>

      <Titulo>Quién recoge qué</Titulo>
      <Parrafo>
        Kodica lo opera la administración de tu fraccionamiento. Nosotros proveemos el sistema y la
        infraestructura donde vive; la administración decide a quién da de alta, qué accesos
        autoriza y durante cuánto tiempo. Si quieres saber por qué estás registrado o quién autorizó
        tu acceso, esa respuesta la tiene tu administración.
      </Parrafo>

      <Titulo>Qué datos se recogen</Titulo>
      <Parrafo>De las personas dadas de alta como residentes o personal:</Parrafo>
      <Lista>
        <li>Nombre y apellidos</li>
        <li>Correo electrónico y teléfono</li>
        <li>La unidad habitacional que ocupas, y tu relación con ella</li>
        <li>Placas, marca, modelo y color de tus vehículos</li>
        <li>El identificador de tu tag vehicular, si usas uno</li>
        <li>Registro de tus entradas y salidas: fecha, hora y carril</li>
      </Lista>
      <Parrafo>De las visitas que llegan a la caseta:</Parrafo>
      <Lista>
        <li>Nombre, y teléfono cuando se proporciona</li>
        <li>Fotografía del rostro, tomada al ingresar</li>
        <li>Fotografía de una identificación oficial</li>
        <li>Placas del vehículo</li>
        <li>A qué unidad se dirige, y quién autorizó el ingreso</li>
      </Lista>
      <Parrafo>Del uso de la aplicación:</Parrafo>
      <Lista>
        <li>Un identificador del dispositivo, para poder enviarte avisos</li>
        <li>La dirección IP desde la que realizas acciones administrativas</li>
      </Lista>

      <Titulo>Datos sensibles: el reconocimiento facial</Titulo>
      <Parrafo>
        En los fraccionamientos donde está instalado, Kodica puede usar una terminal de
        reconocimiento facial para abrir el acceso peatonal. Para eso se guarda una fotografía de tu
        rostro y se registra en el equipo de la reja.
      </Parrafo>
      <Parrafo>
        <strong>Eso son datos biométricos, y la ley los considera sensibles.</strong> Solo se
        recogen si diste tu consentimiento expreso y por escrito, y el enrolamiento facial es
        siempre voluntario: puedes entrar con tag, con código QR o identificándote en la caseta. Si
        te enrolaste y quieres dejar de estarlo, pídelo a tu administración y tu rostro se elimina
        del sistema y de los equipos.
      </Parrafo>

      <Titulo>Para qué se usan</Titulo>
      <Parrafo>Estas son las finalidades necesarias para que el servicio exista:</Parrafo>
      <Lista>
        <li>Controlar y autorizar el acceso al fraccionamiento</li>
        <li>Avisarte cuando llega una visita a tu domicilio, con su fotografía</li>
        <li>Dejar constancia de quién entró y cuándo, para consulta e investigación posterior</li>
        <li>Permitir que la administración te envíe comunicados</li>
        <li>Verificar tu identidad y permitirte recuperar tu contraseña</li>
      </Lista>
      <Parrafo>
        <strong>
          No usamos tus datos para publicidad, ni los vendemos, ni los cedemos a terceros con fines
          comerciales.
        </strong>{' '}
        No hay finalidades secundarias: si algún día las hubiera, este aviso lo diría y podrías
        negarte sin perder el servicio.
      </Parrafo>

      <Titulo>Dónde viven y cuánto duran</Titulo>
      <Parrafo>
        La mayor parte de la información vive{' '}
        <strong>en el servidor instalado dentro de tu fraccionamiento</strong>, no en internet. Esa
        es una decisión de diseño: la reja tiene que funcionar aunque se caiga el enlace.
      </Parrafo>
      <Lista>
        <li>
          <strong>
            La fotografía de identificación de las visitas nunca sale del fraccionamiento.
          </strong>{' '}
          Se queda en el servidor local.
        </li>
        <li>
          <strong>La fotografía del rostro sí sube</strong>, para que puedas verla en tu teléfono, y{' '}
          <strong>se borra a los siete días</strong>. Pasado ese plazo solo existe en el
          fraccionamiento.
        </li>
        <li>
          Las fotografías guardadas localmente se conservan el tiempo que fije tu administración,
          por omisión noventa días, y después se eliminan solas.
        </li>
        <li>
          <strong>Las llamadas a tu teléfono desde la caseta no se graban</strong> ni salen del
          fraccionamiento.
        </li>
        <li>
          El registro de entradas y salidas se conserva mientras tu unidad esté dada de alta, porque
          es el histórico que la administración necesita para investigar un incidente.
        </li>
      </Lista>

      <Titulo>Quién más los ve</Titulo>
      <Parrafo>
        Para operar necesitamos proveedores de infraestructura. Actúan por cuenta nuestra, no pueden
        usar tus datos para otra cosa, y solo reciben lo mínimo:
      </Parrafo>
      <Lista>
        <li>
          <strong>Google</strong>, para entregar los avisos al teléfono. Recibe el identificador de
          tu dispositivo y el texto del aviso. La fotografía del rostro no viaja en el aviso: la
          descarga tu aplicación después, con tu sesión.
        </li>
        <li>
          <strong>Twilio</strong>, para enviarte el código de recuperación de contraseña por
          mensaje. Recibe tu número y el texto del mensaje.
        </li>
        <li>
          <strong>Nuestro proveedor de servidores</strong>, donde vive la parte en línea del
          sistema.
        </li>
      </Lista>
      <Parrafo>
        Fuera de eso, tus datos solo se entregan a la administración de tu fraccionamiento —que es
        quien opera el sistema— y a las autoridades cuando lo requieran conforme a la ley.
      </Parrafo>

      <Titulo>Tus derechos</Titulo>
      <Parrafo>
        Puedes pedir <strong>acceder</strong> a tus datos, <strong>rectificarlos</strong> si son
        inexactos, <strong>cancelarlos</strong> cuando ya no deban tratarse, y{' '}
        <strong>oponerte</strong> a un uso concreto. También puedes revocar tu consentimiento,
        especialmente el del reconocimiento facial.
      </Parrafo>
      <Parrafo>
        Escríbenos a <Correo>contacto@texelos.com</Correo> indicando tu nombre, tu unidad y qué
        necesitas. Te contestaremos en un plazo máximo de veinte días hábiles.
      </Parrafo>
      <Parrafo>
        Si consideras que tu derecho no fue atendido, puedes acudir a la Secretaría Anticorrupción y
        Buen Gobierno, que es la autoridad en materia de protección de datos personales en posesión
        de los particulares.
      </Parrafo>

      <Titulo>Cambios a este aviso</Titulo>
      <Parrafo>
        Si cambia lo que el sistema hace con tus datos, este aviso cambia con él y la fecha de
        arriba lo refleja. Los cambios se publican en esta misma dirección, y cuando afecten a algo
        importante te lo diremos también por la aplicación.
      </Parrafo>
    </div>
  );
}
